"use client";

import {
	AddToCartButtonView,
	type AddToCartButtonState
} from "@/composites/cart/AddToCartButton";
import { QuantitySelector } from "@/composites/cart/QuantitySelector";
import { PriceDisplay } from "@/composites/product/PriceDisplay";
import {
	ProductMessages,
	type ProductMessage
} from "@/composites/product/ProductMessages";
import { ProductRating } from "@/composites/product/ProductRating";
import {
	ProductHighlightTag,
	type HighlightTag
} from "@/composites/product/ProductHighlightTag";
import { Heading, Tag, Text } from "@pamperedchef/design-system";
import type { ReactNode } from "react";
import * as styles from "./ProductSummary.module.scss";

export interface ProductSummaryViewProps {
	title: string;
	sku: string;
	rating?: {
		value: number;
		count: number;
		reviewsHref?: string;
	};
	/** Omit to hide the price row entirely (pre-launch, discontinued). */
	pricing?: {
		display?: number;
		strikeThrough?: number;
		currency?: string;
		locale?: string;
		halfPriceCombo?: boolean;
		kitCredit?: boolean;
	};
	/** Promotional highlight tags (pre-filtered and date-resolved). */
	highlightTags?: HighlightTag[];
	/** Message rows shown above Add to Cart (pre-filtered). */
	messages?: ProductMessage[];
	/** Status tag shown under the price row when the SKU isn't purchasable. */
	availabilityTag?: {
		label: string;
		color: "error" | "grey";
	};
	/** Omit to hide the quantity selector entirely (non-purchasable states). */
	quantity?: {
		value: number;
		onChange: (next: number) => void;
		min?: number;
		max?: number;
		disabled?: boolean;
		errorMessage?: string;
	};
	addToCart: {
		state: AddToCartButtonState;
		onClick: () => void;
	};
	addToList?: ReactNode;
	paymentMessage?: ReactNode;
	className?: string;
}

export function ProductSummaryView({
	title,
	sku,
	rating,
	pricing,
	highlightTags,
	messages,
	availabilityTag,
	quantity,
	addToCart,
	addToList,
	paymentMessage,
	className
}: ProductSummaryViewProps) {
	return (
		<section
			className={[styles["summary"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="ProductSummaryView"
			aria-labelledby="product-summary-title"
		>
			<div className={styles["summary__details"]}>
				<div className={styles["summary__header"]}>
					<Heading
						tag="h1"
						id="product-summary-title"
						className={styles["summary__title"]}
					>
						{title}
					</Heading>

					<Text
						tag="p"
						styledAs="smr"
						className={styles["summary__sku"]}
					>
						Item Number: #{sku}
					</Text>

					{rating ? (
						<ProductRating
							value={rating.value}
							count={rating.count}
							reviewsHref={rating.reviewsHref}
						/>
					) : null}
				</div>

				{highlightTags && highlightTags.length > 0 ? (
					<ProductHighlightTag tags={highlightTags} />
				) : null}

				{pricing ? (
					<PriceDisplay
						display={pricing.display}
						strikeThrough={pricing.strikeThrough}
						currency={pricing.currency}
						locale={pricing.locale}
						halfPriceCombo={pricing.halfPriceCombo}
						kitCredit={pricing.kitCredit}
						size="large"
					/>
				) : null}

				{availabilityTag ? (
					<Tag
						label={availabilityTag.label}
						color={availabilityTag.color}
						kind="subtle"
						size="md"
						icon={{ name: "alert-circle" }}
						className={styles["summary__availability"]}
					/>
				) : null}
			</div>

			{messages && messages.length > 0 ? (
				<ProductMessages messages={messages} />
			) : null}

			<div className={styles["summary__cart"]}>
				{quantity ? (
					<div className={styles["summary__qty"]}>
						<QuantitySelector
							value={quantity.value}
							onChange={quantity.onChange}
							min={quantity.min}
							max={quantity.max}
							disabled={quantity.disabled}
							errorMessage={quantity.errorMessage}
						/>
					</div>
				) : null}
				<AddToCartButtonView
					state={addToCart.state}
					onClick={addToCart.onClick}
					fullWidth
				/>
			</div>
			{paymentMessage}
			{addToList ? (
				<div className={styles["summary__lists"]}>{addToList}</div>
			) : null}
		</section>
	);
}
